import React, { useState, useRef } from 'react';
import { ChevronDown, AlertCircle, Loader2 } from 'lucide-react';
import { Switch } from '../../ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

const ProcessStreetSection = ({
  formData,
  errors,
  isSubmitting,
  handleInputChange,
  // Plan number props
  planSuggestionsRef,
  handlePlanNumberSearch,
  handlePlanKeyDown,
  showPlanSuggestions,
  planNumberSuggestions,
  selectedSuggestionIndex,
  handlePlanNumberSelect,
  // Workflow run props
  workflowRunInput,
  setWorkflowRunInput,
  showWorkflowRunSuggestions,
  setShowWorkflowRunSuggestions,
  workflowRuns,
  selectedWorkflowRunSuggestionIndex,
  setSelectedWorkflowRunSuggestionIndex,
  isLoadingWorkflowRuns,
  handleWorkflowRunSelect,
  isDark
}) => {
  const [isPlanTypeOpen, setIsPlanTypeOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const isSelectingRef = useRef(false);
  const planTypeOptions = ['SP', 'DP', 'CA'];

  return (
    <>
    <div className={`border-none rounded-[16px] p-[20px] flex flex-col gap-[16px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white'
    }`}>
      {/* Header */}
      <p className={`m-0 font-semibold text-[16px] leading-normal shrink-0 whitespace-pre transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`}>
        Do you want to preload data in the form?
      </p>

      {/* Toggle Section */}
      <div className={`border-none rounded-[8px] p-[12px] flex items-center justify-between w-full max-w-[782px] shrink-0 transition-colors duration-200 ${
        isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[f8f9fa]'
      }`}>
        <div className="basis-0 flex gap-[16px] grow items-center min-h-px min-w-px shrink-0">
          <p className={`basis-0 font-medium text-[16px] grow leading-normal min-h-px min-w-px shrink-0 m-0 transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>
            Preload form from process street
          </p>
          <Switch
            checked={formData.preload_from_process_street}
            onCheckedChange={(checked) => handleInputChange('preload_from_process_street', checked)}
          />
        </div>
      </div>
    </div>

    {/* Expanded Section when toggled on */}
    {formData.preload_from_process_street && (
      <div className={`border-none rounded-[16px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col gap-[16px] transition-colors duration-200 ${
        isDark ? 'bg-[#000000]' : 'bg-white'
      }`}>
          {/* Plan Number Field */}
          <div className="flex flex-col gap-[16px]">
            <h4 className={`m-0 font-semibold text-[16px] transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>
              Search plan to preload form <span className="text-red-600">*</span>
            </h4>

            <div className="relative w-full" ref={planSuggestionsRef}>
              <div className={`border-none rounded-[8px] p-[12px] flex items-center gap-[16px] w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
                isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
              }`}>
                {/* Plan Type Dropdown */}
                <DropdownMenu open={isPlanTypeOpen} onOpenChange={setIsPlanTypeOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={`flex items-center gap-[6px] shrink-0 font-medium text-[16px] bg-transparent border-0 outline-none cursor-pointer hover:opacity-80 transition-all duration-200 ${
                        isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                      }`}
                    >
                      <span>{formData.planType || 'SP'}</span>
                      <ChevronDown 
                        className={`w-[12px] h-[12px] transition-all duration-200 ${isPlanTypeOpen ? 'rotate-180' : ''} ${
                          isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                        }`}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="start" 
                    className={`min-w-[80px] rounded-[8px] p-0 shadow-lg transition-colors duration-200 ${
                      isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#d9d9d9]'
                    }`}
                  >
                    {planTypeOptions.map((option) => (
                      <DropdownMenuItem
                        key={option}
                        className={`font-medium text-[16px] px-[12px] py-[8px] cursor-pointer transition-colors duration-200 ${
                          formData.planType === option 
                            ? isDark
                              ? 'bg-[#cd6d2c] text-[#000000]'
                              : 'bg-[#072837] text-white'
                            : isDark
                              ? 'bg-[#161515] text-white hover:bg-[#222222] hover:text-[#cd6d2c]'
                              : 'bg-white text-[#072837] hover:bg-[#f5f5f5]'
                        }`}
                        onClick={() => {
                          handleInputChange('planType', option);
                          setIsPlanTypeOpen(false);
                        }}
                      >
                        {option}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Divider */}
                <div className={`w-[1px] h-[30px] shrink-0 transition-colors duration-200 ${
                  isDark ? 'bg-[#292929]' : 'bg-[#d9d9d9]'
                }`}></div>

                {/* Plan Number Input */}
                <input
                  className={`grow basis-0 min-w-0 font-normal text-[16px] bg-transparent border-0 outline-none transition-colors duration-200 ${
                    isDark ? 'text-white placeholder:text-[#b3b3b3]' : 'text-[#686868] placeholder:text-[#686868]'
                  }`}
                  type="text"
                  placeholder="Enter Plan no."
                  value={formData.planNumber}
                  onChange={(e) => handlePlanNumberSearch(e.target.value)}
                  onKeyDown={handlePlanKeyDown}
                />
              </div>

              {/* Plan Suggestions Dropdown */}
              {showPlanSuggestions && planNumberSuggestions.length > 0 && (
                <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                  isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                }`}>
                  {planNumberSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className={`px-3 py-2 font-medium text-[14px] cursor-pointer transition-colors duration-200 ${
                        index === selectedSuggestionIndex 
                          ? isDark
                            ? 'bg-[#cd6d2c] text-[#000000]'
                            : 'bg-[#072837] text-white'
                          : isDark
                            ? 'bg-[#161515] text-white hover:bg-[#292929] hover:text-[#000000]'
                            : 'bg-white text-[#072837] hover:!bg-[#072837] hover:!text-[#ffffff]'
                      }`}
                      onClick={() => handlePlanNumberSelect(suggestion)}
                    >
                      {suggestion.plan_number} | {suggestion.database_name.replace(/_/g, ' ').toUpperCase()} | {suggestion.address.trim()}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* Workflow Run Field */}
          <div className="flex flex-col gap-[16px]">
            <h4 className={`m-0 font-semibold text-[16px] transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>Select Workflow Run</h4>

            <div className="relative w-full">
              <div className={`border-none rounded-[8px] p-[12px] flex items-center justify-between relative w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
                isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
              }`}>
                <input
                  className={`grow basis-0 min-w-0 font-normal text-[16px] bg-transparent border-0 outline-none pr-[40px] transition-colors duration-200 ${
                    isDark ? 'text-white placeholder:text-[#b3b3b3]' : 'text-[#686868] placeholder:text-[#686868]'
                  }`}
                  type="text"
                  placeholder="Search and select workflow run"
                  value={workflowRunInput}
                  disabled={isSubmitting}
                  onChange={(e) => {
                    setIsSearchActive(true);
                    setWorkflowRunInput(e.target.value);
                  }}
                  onFocus={() => {
                    setIsSearchActive(false);
                    setShowWorkflowRunSuggestions(workflowRuns.length > 0);
                    setSelectedWorkflowRunSuggestionIndex(-1);
                  }}
                  onKeyDown={(e) => {
                    if (!showWorkflowRunSuggestions || workflowRuns.length === 0) return;

                    const filteredRuns = workflowRuns.filter(run => {
                      if (!workflowRunInput) return true;
                      const searchTerm = workflowRunInput.toLowerCase();
                      const name = (run.name || `Workflow ${run.id}`).toLowerCase();
                      return name.includes(searchTerm);
                    });

                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelectedWorkflowRunSuggestionIndex(prev =>
                        prev < filteredRuns.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSelectedWorkflowRunSuggestionIndex(prev => prev > 0 ? prev - 1 : 0);
                    } else if (e.key === 'Enter' && selectedWorkflowRunSuggestionIndex >= 0) {
                      e.preventDefault();
                      const selected = filteredRuns[selectedWorkflowRunSuggestionIndex];
                      if (selected) {
                        handleWorkflowRunSelect(selected.id, selected.name || `Workflow ${selected.id}`);
                      }
                    } else if (e.key === 'Escape') {
                      setShowWorkflowRunSuggestions(false);
                      setSelectedWorkflowRunSuggestionIndex(-1);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowWorkflowRunSuggestions(false);
                      setIsSearchActive(false);
                      // Only restore name if user dismissed without selecting a new item
                      if (!isSelectingRef.current && formData.process_street_workflow_run) {
                        const currentRun = workflowRuns.find(r => r.id === formData.process_street_workflow_run);
                        if (currentRun) setWorkflowRunInput(currentRun.name || `Workflow ${currentRun.id}`);
                      }
                      isSelectingRef.current = false;
                    }, 150);
                  }}
                  autoComplete="off"
                />

                {/* Dropdown Icon */}
                <span
                  className="absolute right-[12px] top-1/2 flex items-center justify-center cursor-pointer transition-transform shrink-0"
                  style={{
                    transform: `translateY(-50%) rotate(${showWorkflowRunSuggestions ? '180deg' : '0deg'})`
                  }}
                >
                  <span
                    className="cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (showWorkflowRunSuggestions) {
                        setShowWorkflowRunSuggestions(false);
                      } else {
                        setShowWorkflowRunSuggestions(workflowRuns.length > 0);
                        setSelectedWorkflowRunSuggestionIndex(-1);
                      }
                    }}
                  >
                    {isLoadingWorkflowRuns ? (
                      <Loader2 className={`w-[18px] h-[18px] animate-spin transition-colors duration-200 ${
                        isDark ? 'text-[#b3b3b3]' : 'text-[#555]'
                      }`} />
                    ) : (
                      <ChevronDown className={`w-[18px] h-[18px] transition-colors duration-200 ${
                        workflowRuns && workflowRuns.length > 0 
                          ? isDark ? 'text-white' : 'text-[#000]'
                          : isDark ? 'text-[#8c8c8c]' : 'text-[#898989]'
                      }`} />
                    )}
                  </span>
                </span>
              </div>

              {/* Workflow Run Suggestions Dropdown */}
              {showWorkflowRunSuggestions && workflowRuns.length > 0 && (
                <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
                  isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
                }`}>
                  {workflowRuns
                    .filter(workflowRun => {
                      if (!isSearchActive || !workflowRunInput) return true;
                      const searchTerm = workflowRunInput.toLowerCase();
                      const name = (workflowRun.name || `Workflow ${workflowRun.id}`).toLowerCase();
                      return name.includes(searchTerm);
                    })
                    .map((workflowRun, index) => {
                      const isSelected = workflowRun.id === formData.process_street_workflow_run;
                      const isKeyboardHighlighted = index === selectedWorkflowRunSuggestionIndex;
                      const isHighlighted = isKeyboardHighlighted || isSelected;
                      return (
                        <div
                          key={workflowRun.id}
                          className={`px-3 py-2 cursor-pointer flex justify-between items-center group transition-colors duration-200 ${
                            isHighlighted
                              ? isDark
                                ? 'bg-[#cd6d2c] text-[#000000]'
                                : 'bg-[#fff5eb] text-[#072837]'
                              : isDark
                                ? 'bg-[#161515] hover:bg-[#292929] text-white'
                                : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                          }`}
                          onMouseDown={() => { isSelectingRef.current = true; }}
                          onClick={() => {
                            handleWorkflowRunSelect(workflowRun.id, workflowRun.name || `Workflow ${workflowRun.id}`);
                          }}
                        >
                          <div className={`font-medium text-[14px] transition-colors duration-200 ${
                            isHighlighted
                              ? isDark ? 'text-[#000000]' : 'text-[#072837]'
                              : isDark ? 'text-white' : 'group-hover:!text-white text-[#072837]'
                          }`}>
                            {workflowRun.name || `Workflow ${workflowRun.id}`}
                          </div>
                          <div className={`font-normal text-[12px] transition-colors duration-200 ${
                            isHighlighted
                              ? isDark ? 'text-[#000000]' : 'text-[#686868]'
                              : isDark ? 'text-[#b3b3b3]' : 'group-hover:!text-white text-[#686868]'
                          }`}>
                            {workflowRun.status || 'Unknown'} • {workflowRun.createdDate ? new Date(workflowRun.createdDate).toLocaleDateString('en-AU') : 'N/A'}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

          </div>
      </div>
    )}
    {formData.preload_from_process_street && errors.planNumber && (
      <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
        <span className="leading-[1.4]">You are required to fill this answer</span>
      </div>
    )}
    {formData.preload_from_process_street && errors.planNumberSuggestion && (
      <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
        <span className="leading-[1.4]">Please select a plan number from the suggestions.</span>
      </div>
    )}
    {formData.preload_from_process_street && errors.process_street_workflow_run && (
      <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
        <span className="leading-[1.4]">Please select a workflow run.</span>
      </div>
    )}
    </>
  );
};

export default ProcessStreetSection;